import { alpha, createTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'tertiary'
  | 'paid'
  | 'organic'
  | 'revenue'
  | 'conversions';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    tertiary: PaletteColor;
    paid: PaletteColor;
    organic: PaletteColor;
    revenue: PaletteColor;
    conversions: PaletteColor;
    darker: PaletteColor;
    default: PaletteColor;
    cpc: PaletteColor;
    metrics: {
      brandSearch: MetricsColor;
      brandTraffic: MetricsColor;
      organicCtr: MetricsColor;
      organicBrandTraffic: MetricsColor;
      paidCtr: MetricsColor;
      shoppingPaidCtr: MetricsColor;
      searchPaidCtr: MetricsColor;
      pmaxPaidCtr: MetricsColor;
      searchAdCost: MetricsColor;
      adCost: MetricsColor;
      shoppingAdCost: MetricsColor;
      pmaxAdCost: MetricsColor;
      cpc: MetricsColor;
      searchCpc: MetricsColor;
      shoppingCpc: MetricsColor;
      paidBrandTraffic: MetricsColor;
      pmaxCpc: MetricsColor;
      searchBrandTraffic: MetricsColor;
      shoppingBrandTraffic: MetricsColor;
      pmaxBrandTraffic: MetricsColor;
      blendedCpc: MetricsColor;
      blendedCtr: MetricsColor;
      brandSearches: MetricsColor;
      totalBrandTraffic: MetricsColor;
      adBrandTraffic: MetricsColor;
      adCostSearch: MetricsColor;
      adCostShopping: MetricsColor;
      adCostPmax: MetricsColor;
      paidCtrShopping: MetricsColor;
      paidCtrPmax: MetricsColor;
      paidCtrSearch: MetricsColor;
      totalOrganicClicks: MetricsColor;
      totalAdClicks: MetricsColor;
      uncapturedBrandTraffic: MetricsColor;
      totalOrganicSearches: MetricsColor;
      totalAdCost: MetricsColor;
    };
  }
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  }
  interface MetricsColor {
    light: string;
    main: string;
    dark: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#006c9c',
  light: '#5BE49B',
  main: '#00A76F',
  dark: '#006c9c',
  darker: '#004B50',
  contrastText: '#FFFFFF',
};

const PAID = {
  lighter: '#66a4d1',
  light: '#3384b8',
  main: '#006c9c',
  dark: '#004b6d',
  darker: '#00314a',
  contrastText: '#FFFFFF',
};

const ORGANIC = {
  lighter: '#0f8e6b', // légèrement plus clair que #065e49
  light: '#0b7f5f',
  main: '#065e49',
  dark: '#044537',
  darker: '#033229',
  contrastText: '#FFFFFF',
};

const REVENUE = {
  lighter: '#a3ccee',
  light: '#64b5f6',
  main: '#64b5f6',
  dark: '#006c9c',
  darker: '#006c9c',
  contrastText: '#FFFFFF',
};

const CONVERSIONS = {
  lighter: '#66a4d1',
  light: '#3384b8',
  main: '#006c9c',
  dark: '#004b6d',
  darker: '#00314a',
  contrastText: '#FFFFFF',
};

const DEFAULT = {
  lighter: '#FFFFFF',
  light: '#FFFFFF',
  main: '#FFFFFF',
  dark: '#FFFFFF',
  darker: '#FFFFFF',
  contrastText: '#FFFFFF',
};

const DARKER = {
  lighter: '#212b36',
  light: '#212b36',
  main: '#212b36',
  dark: '#212b36',
  darker: '#212b36',
  contrastText: '#212b36',
};

const TERTIARY = {
  lighter: '#cafdf5',
  light: '#006c9c',
  main: '#68CDF9',
  dark: '#006c9c',
  darker: '#006c9c',
  contrastText: '#006c9c',
};

const SECONDARY = {
  lighter: '#fad1e7',
  light: '#fad1e7',
  main: '#f73378',
  dark: '#880e4f',
  darker: '#880e4f',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#efd6ff',
  light: '#5119b7',
  main: '#8E33FF',
  dark: '#5119b7',
  darker: '#5119b7',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#d3fcd2',
  light: '#118d57',
  main: '#22C55E',
  dark: '#118d57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#fff5cc',
  light: '#ffab00',
  main: '#FFD666',
  dark: '#ffab00',
  darker: '#ffab00',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#ffe9d5',
  light: '#b71d18',
  main: '#FF5630',
  dark: '#b71d18',
  darker: '#b71d18',
  contrastText: '#FFFFFF',
};

// Custom metrics colors (copied from original)
const METRICS = {
  blendedCtr: {
    light: '#5119b7',
    main: '#8E33FF',
    dark: '#5119b7'
  },
  blendedCpc: {
    light: '#b76e00',
    main: '#FFD666',
    dark: '#b76e00'
  },
  adCost: {
    light: '#b76e00',
    main: '#FFD666',
    dark: '#b76e00'
  },
  searchAdCost: {
    light: '#1e88e5',
    main: '#1e88e5',
    dark: '#1e88e5'
  },
  shoppingAdCost: {
    lighter: '#FFF2CA',
    light: '#FFE595',
    main: '#FFD54F',
    dark: '#B29537',
    darker: '#FFD54F',
  },
  pmaxAdCost: {
    lighter: '#FFF6D8',
    light: '#FFEDB2',
    main: '#FFE180',
    dark: '#B29D59',
    darker: '#FFE180',
  },
  cpc: {
    lighter: '#FFCCC0',
    light: '#FF9982',
    main: '#FF5630',
    dark: '#B23C21',
    darker: '#FF5630',
  },
  searchCpc: {
    lighter: '#FFCCC0',
    light: '#FF9982',
    main: '#FF5630',
    dark: '#B23C21',
    darker: '#FF5630',
  },
  shoppingCpc: {
    lighter: '#FFE6D9',
    light: '#FFCDB4',
    main: '#FFAC82',
    dark: '#B2785B',
    darker: '#FFAC82',
  },
  pmaxCpc: {
    lighter: '#FFF8F2',
    light: '#FFF1E5',
    main: '#FFE9D5',
    dark: '#B2A395',
    darker: '#FFE9D5',
  },
  searchBrandTraffic: {
    lighter: '#BBDBF7',
    light: '#78B7EF',
    main: '#1E88E5',
    dark: '#155FA0',
    darker: '#1E88E5',
  },
  shoppingBrandTraffic: {
    lighter: '#D0E8FC',
    light: '#A2D2F9',
    main: '#64B5F6',
    dark: '#467EAC',
    darker: '#64B5F6',
  },
  pmaxBrandTraffic: {
    lighter: '#FFFFFF',
    light: '#ECF6FE',
    main: '#B7DCFB',
    dark: '#2B99F3',
    darker: '#B7DCFB',
  },
  brandSearches: {
    lighter: '#E9BBB9',
    light: '#D37774',
    main: '#B71D18',
    dark: '#801410',
    darker: '#B71D18',
  },
  totalBrandTraffic: {
    lighter: '#DBB6CA',
    light: '#B76E95',
    main: '#880E4F',
    dark: '#5F0937',
    darker: '#880E4F',
  },
  adBrandTraffic: {
    lighter: '#B2D2E1',
    light: '#66A6C3',
    main: '#006C9C',
    dark: '#004B6D',
    darker: '#006C9C',
  },
  adCostSearch: {
    lighter: '#FFE8B2',
    light: '#FFD166',
    main: '#FFB300',
    dark: '#B27D00',
    darker: '#FFB300',
  },
  adCostShopping: {
    lighter: '#FFF2CA',
    light: '#FFE595',
    main: '#FFD54F',
    dark: '#B29537',
    darker: '#FFD54F',
  },
  adCostPmax: {
    lighter: '#FFF6D8',
    light: '#FFEDB2',
    main: '#FFE180',
    dark: '#B29D59',
    darker: '#FFE180',
  },
  paidCtrShopping: {
    lighter: '#D0E8FC',
    light: '#A2D2F9',
    main: '#64B5F6',
    dark: '#467EAC',
    darker: '#64B5F6',
  },
  paidCtrPmax: {
    lighter: '#E9F4FD',
    light: '#D3EAFC',
    main: '#B7DCFB',
    dark: '#809AAF',
    darker: '#B7DCFB',
  },
  paidCtrSearch: {
    lighter: '#BBDBF7',
    light: '#78B7EF',
    main: '#1E88E5',
    dark: '#155FA0',
    darker: '#1E88E5',
  },
  totalOrganicClicks: {
    lighter: '#B7DCCC',
    light: '#70BA9A',
    main: '#118D57',
    dark: '#0B623C',
    darker: '#118D57',
  },
  totalAdClicks: {
    lighter: '#B2D2E1',
    light: '#66A6C3',
    main: '#006C9C',
    dark: '#004B6D',
    darker: '#006C9C',
  },
  uncapturedBrandTraffic: {
    lighter: '#E9BBB9',
    light: '#D37774',
    main: '#B71D18',
    dark: '#801410',
    darker: '#B71D18',
  },
  totalOrganicSearches: {
    lighter: '#B7DCCC',
    light: '#70BA9A',
    main: '#118D57',
    dark: '#0B623C',
    darker: '#118D57',
  },
  totalAdCost: {
    lighter: '#FFE5B2',
    light: '#FFCC66',
    main: '#FFAB00',
    dark: '#B27700',
    darker: '#FFAB00',
  },
};

const CPC = {
  lighter: '#FFAC99',
  light: '#FF8A70',
  main: '#ff9b85',
  dark: '#E02900',
  darker: '#FF5630',
  contrastText: '#FF5630',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  default: DEFAULT,
  tertiary: TERTIARY,
  darker: DARKER,
  paid: PAID,
  organic: ORGANIC,
  revenue: REVENUE,
  conversions: CONVERSIONS,
  grey: GREY,
  metrics: METRICS,
  cpc: CPC,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const facebook = createTheme().palette.augmentColor({
  color: { main: '#1877F2', lighter: '#1889f2', darker: '#183cf2' },
});

const lightbutton = createTheme().palette.augmentColor({
  color: { main: '#dedede', lighter: '#d9d9d9', darker: '#a6a6a6' },
});

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
    facebook,
    lightbutton,
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    facebook,
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
